import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import Header from '../../components/Header';
import StatBox from '../../components/StatBox';
import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { tokens } from '../../theme';
import FilterListIcon from '@mui/icons-material/FilterList';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentsIcon from '@mui/icons-material/Payments';
import PeopleIcon from '@mui/icons-material/People';
import DownloadIcon from '@mui/icons-material/Download';

const API_BASE = `${process.env.REACT_APP_APILINK}/admin/coordinator-expenses`;

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const CoordinatorView = () => {
  const { id } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');
  const now = new Date();
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [expensesLoading, setExpensesLoading] = useState(true);
  const [expensesPage, setExpensesPage] = useState(1);
  const [expensesPageSize, setExpensesPageSize] = useState(25);
  const [expensesTotalCount, setExpensesTotalCount] = useState(0);
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchCoordinator = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/adminusers/${id}`, {
          headers: { token },
        });
        const data = await res.json();
        if (data.success && Number(data.data?.role) === 3) {
          setDetails(data.data);
        } else {
          setDetails(null);
        }
      } catch (e) {
        setDetails(null);
      } finally {
        setLoading(false);
      }
    };
    fetchCoordinator();
  }, [id, token]);

  useEffect(() => {
    if (!id) return;
    const fetchExpenses = async () => {
      setExpensesLoading(true);
      try {
        const params = new URLSearchParams({ coordinatorId: id });
        if (filterMonth) params.set('month', filterMonth);
        if (filterYear) params.set('year', filterYear);
        params.set('page', String(expensesPage));
        params.set('limit', String(expensesPageSize));
        const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: { token } });
        const data = await res.json();
        if (data.success) {
          setExpenses(data.data || []);
          setExpensesTotalCount(data.pagination?.total || 0);
        } else {
          setExpenses([]);
        }
      } catch (e) {
        setExpenses([]);
      } finally {
        setExpensesLoading(false);
      }
    };
    fetchExpenses();
  }, [id, token, filterMonth, filterYear, expensesPage, expensesPageSize]);

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '');
  const totalAmount = expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);
  const selectedMonthLabel = MONTHS.find((m) => String(m.value) === filterMonth)?.label || '';
  const filterLabel =
    filterMonth && filterYear ? `${selectedMonthLabel} ${filterYear}`.trim() : 'All';
  const subtitle = `${details?.username || 'Coordinator'} — expenses for ${filterLabel}`;

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) {
    years.push(y);
  }

  const columns = [
    { field: 'id', headerName: 'ID', width: 70, hide: true },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 180 },
    {
      field: 'amount',
      headerName: 'Amount (€)',
      width: 110,
      valueFormatter: (params) =>
        params.value != null ? `€ ${Number(params.value).toFixed(2)}` : '',
    },
    {
      field: 'created_at',
      headerName: 'Date',
      width: 120,
      valueFormatter: (params) => formatDate(params.value),
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress sx={{ color: colors.grey[100] }} />
      </Box>
    );
  }

  if (!details || Number(details.role) !== 3) {
    return (
      <Box m="20px">
        <Header title="Coordinator View" subtitle="Coordinator not found or invalid" />
        <Button
          component={Link}
          to="/adminusers"
          variant="contained"
          sx={{
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            mt: 2,
            '&:hover': { backgroundColor: colors.blueAccent[800] },
          }}
        >
          Back to Admin Users
        </Button>
      </Box>
    );
  }

  const assignedGuidesCount = Array.isArray(details.guides) ? details.guides.length : 0;

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ coordinatorId: id });
      if (filterMonth) params.set('month', filterMonth);
      if (filterYear) params.set('year', filterYear);
      const url = `${API_BASE}/export-excel?${params.toString()}`;
      const res = await fetch(url, { headers: { token } });
      if (!res.ok) return;
      const blob = await res.blob();
      const disp = res.headers.get('Content-Disposition');
      const match = disp && disp.match(/filename="?([^";\n]+)"?/);
      const filename = match ? match[1].trim() : 'coordinator-expenses.xlsx';
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(href);
    } catch (e) {
      console.error('Export Excel error', e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" gap="10px" flexWrap="wrap">
        <Header title="Coordinator View" subtitle={subtitle} />
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportExcel}
            disabled={exporting}
            sx={{
              backgroundColor: colors.greenAccent[600],
              color: colors.grey[100],
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '10px 20px',
              '&:hover': { backgroundColor: colors.greenAccent[700] },
            }}
          >
            {exporting ? 'Exporting…' : 'Export Excel'}
          </Button>
          <Button
            component={Link}
            to="/adminusers"
            variant="contained"
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '10px 20px',
              '&:hover': { backgroundColor: colors.blueAccent[800] },
            }}
          >
            Back to Admin Users
          </Button>
        </Box>
      </Box>

      {/* Summary cards - same style as dashboard (narrow width) */}
      <Box mt={4} p={2} sx={{ background: colors.primary[500], borderRadius: 0, boxShadow: 'none', mb: 1 }}>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: 'repeat(1, minmax(0, 260px))',
            sm: 'repeat(2, minmax(0, 260px))',
            md: 'repeat(3, minmax(0, 260px))',
          }}
          gap={2}
        >
          <StatBox
            title={expensesLoading ? '...' : expenses.length}
            subtitle="Total Expenses"
            icon={<ReceiptLongIcon sx={{ color: colors.greenAccent[600], fontSize: 40 }} />}
          />
          <StatBox
            title={expensesLoading ? '...' : `€ ${Number(totalAmount).toFixed(2)}`}
            subtitle="Total Amount"
            icon={<PaymentsIcon sx={{ color: colors.greenAccent[600], fontSize: 40 }} />}
          />
          <StatBox
            title={assignedGuidesCount}
            subtitle="Assigned Guides"
            icon={<PeopleIcon sx={{ color: colors.greenAccent[600], fontSize: 40 }} />}
          />
        </Box>
      </Box>

      <Box
        sx={{
          mt: 2,
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: colors.grey[100],
            fontWeight: 600,
          }}
        >
          <FilterListIcon /> FILTERS
        </Typography>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="month-filter-label">Month</InputLabel>
          <Select
            labelId="month-filter-label"
            value={filterMonth}
            label="Month"
            onChange={(e) => setFilterMonth(e.target.value)}
            sx={{
              backgroundColor: colors.primary[400],
              color: colors.grey[100],
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[700] },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] },
            }}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {MONTHS.map((m) => (
              <MenuItem key={m.value} value={String(m.value)}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="year-filter-label">Year</InputLabel>
          <Select
            labelId="year-filter-label"
            value={filterYear}
            label="Year"
            onChange={(e) => setFilterYear(e.target.value)}
            sx={{
              backgroundColor: colors.primary[400],
              color: colors.grey[100],
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[700] },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] },
            }}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {years.map((y) => (
              <MenuItem key={y} value={String(y)}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box
        m="10px 0 0 0"
        height="667px"
        sx={{
          '& .MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-cell': { borderBottom: 'none' },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.blueAccent[700],
            borderBottom: 'none',
          },
          '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
            backgroundColor: colors.blueAccent[700],
          },
        }}
      >
        <DataGrid
          rows={expenses}
          columns={columns}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          disableColumnFilter
          loading={expensesLoading}
          paginationMode="server"
          page={expensesPage - 1}
          pageSize={expensesPageSize}
          rowCount={expensesTotalCount}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(p) => setExpensesPage(p + 1)}
          onPageSizeChange={(s) => { setExpensesPageSize(s); setExpensesPage(1); }}
        />
      </Box>
    </Box>
  );
};

export default CoordinatorView;
